import {useAddComment, CommentsCategory} from '@domain/comments'
import {usePlantConfig} from '@domain/shifthandover'
import {DataContentWrapper} from '@hconnect/common/containers'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {SubmitHandler} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {ProcessWorkOrderForm} from '../../components/form'
import {TaskResolvedMandatoryComment} from '../../components/TaskDetails/TaskResolvedMandatoryComment'
import {EditTaskCard} from '../../components/TaskEdit/EditTaskCard'
import {FEATURE_NAMESPACE} from '../../consts'
import {useProcessWorkOrder, useUpdateProcessWorkOrder} from '../../hooks'
import {mapToProcessWorkOrderEditDto, mapToProcessWorkOrderFormValues} from '../../mappers'
import {
  SideCardModes,
  SideCardItemMode,
  HProduceTask,
  ProcessWorkOrderFormValues,
  TaskSource,
  TaskStatus,
  HProduceTaskIdentifiers
} from '../../types'
import {convertAttachmentsToFiles} from '../../utils'
import {ProcessWorkOrderCommentsQueryKeyPrefix} from '../ProcessWorkOrderCommentsContainer'

type Props = {
  task: HProduceTaskIdentifiers
  onClose: (original?: HProduceTask, editVersion?: HProduceTask) => void
  onTaskTypeChange: (taskType: TaskSource | undefined) => void
  isSingleEvent: boolean
  setMode: (next: SideCardModes) => void
}

export const ProcessWorkOrderEditContainer = ({
  task,
  onClose,
  onTaskTypeChange,
  isSingleEvent,
  setMode
}: Props) => {
  const {t, i18n} = useTranslation(FEATURE_NAMESPACE)
  const {data, isLoading: isLoadingPWO, isError, refetch, isRefetching} = useProcessWorkOrder(task)
  const {data: configData, isLoading: isLoadingConfig} = usePlantConfig({
    plantId: task.plantId,
    language: i18n.language
  })
  const {mutate: updateProcessWorkOrder} = useUpdateProcessWorkOrder()
  const {mutateAsync: addComment} = useAddComment(
    {
      plantId: task.plantId,
      commentsCategory: CommentsCategory.ProcessWorkOrders
    },
    ProcessWorkOrderCommentsQueryKeyPrefix
  )

  const [isCommentDialogOpen, setIsCommentDialogOpen] = useState(false)
  const [statusComment, setStatusComment] = useState<string | null>(null)
  const resetStatusRef = useRef<(() => void) | null>(null)

  const handleCancel = () => {
    setMode({mode: SideCardItemMode.Details, itemId: task.id})
  }

  const handleStatusChange = useCallback(
    (newValue: TaskStatus | undefined, resetStatus: () => void) => {
      if (!data) return

      const isChangingToDone = data.status !== newValue && newValue === TaskStatus.Done

      if (isChangingToDone) {
        resetStatusRef.current = resetStatus
        setIsCommentDialogOpen(true)
      } else {
        setStatusComment(null)
      }
    },
    [data]
  )

  const handleCommentDialogClose = useCallback(() => {
    setIsCommentDialogOpen(false)
    resetStatusRef.current?.()
    resetStatusRef.current = null
  }, [])

  const handleCommentSubmit = useCallback((comment: string) => {
    setStatusComment(comment)
    setIsCommentDialogOpen(false)
    resetStatusRef.current = null
  }, [])

  const handleSubmit: SubmitHandler<ProcessWorkOrderFormValues> = async (submittedData) => {
    if (!data) return

    const dto = mapToProcessWorkOrderEditDto(submittedData, data, initialValues)
    dto.isRecurring = !isSingleEvent

    const isStatusChangingToDone =
      data.status !== submittedData.status && submittedData.status === TaskStatus.Done

    await new Promise<void>((resolve, reject) => {
      updateProcessWorkOrder(dto, {
        onSuccess: async (updatedWorkOrder) => {
          if (isStatusChangingToDone && statusComment) {
            await addComment({value: statusComment, commentCategoryInstanceId: updatedWorkOrder.id})
          }
          setMode({mode: SideCardItemMode.Details, itemId: task.id})
          resolve()
        },
        onError: reject
      })
    })
  }

  const initialValues = useMemo(() => {
    return data && configData?.timezone
      ? mapToProcessWorkOrderFormValues(data, configData?.timezone)
      : undefined
  }, [data, configData?.timezone])
  const [convertedAttachments, setConvertedAttachments] = useState<File[]>([])

  useEffect(() => {
    let isMounted = true
    const convert = async () => {
      if (initialValues?.attachments) {
        const files = await convertAttachmentsToFiles(initialValues.attachments)
        if (isMounted) setConvertedAttachments(files)
      } else {
        setConvertedAttachments([])
      }
    }
    void convert()
    return () => {
      isMounted = false
    }
  }, [initialValues?.attachments])

  return (
    <>
      <DataContentWrapper
        data={data}
        showLoader={isLoadingPWO || isLoadingConfig}
        isError={isError}
        retryFunction={refetch}
        isRefetching={isRefetching}
        noDataSettings={{
          noDataMessage: t('task.message.taskNotFound'),
          onActionClick: () => onClose()
        }}
      >
        {data && configData?.timezone && (
          <EditTaskCard title={data.title}>
            <ProcessWorkOrderForm
              externalTaskId={task.externalId}
              onSubmit={handleSubmit}
              onCancel={handleCancel}
              onTaskTypeChange={onTaskTypeChange}
              initialValues={{...initialValues, attachments: convertedAttachments}}
              initialAttachments={data.attachments}
              disableRepetition={isSingleEvent}
              disableRecurringToggle={!isSingleEvent}
              disableChecklistsSelect={true}
              plantTimezone={configData.timezone}
              onStatusChange={handleStatusChange}
            />
          </EditTaskCard>
        )}
      </DataContentWrapper>
      <TaskResolvedMandatoryComment
        data-test-id="task-resolved-mandatory-comment-modal"
        open={isCommentDialogOpen}
        onClose={handleCommentDialogClose}
        onSubmit={handleCommentSubmit}
      />
    </>
  )
}
